import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { i18n } from '@fs/zion-locale'
import { Grid, Separator, DataPreview, Skeleton, H2 } from '@fs/zion-ui'
import { Link } from '@fs/zion-router'
import ProgressBar from 'components/ProgressBar/ProgressBar'
import { toSearchResultsWithProjectId } from '../../helpers/pathHelper'
import transl from './ProjectInfo.transl'
import styles from '../../views/CameraOperatorProjectsDashboard/CameraOperatorProjectsDashoard.styles'

const loader = (
  <>
    <Skeleton.H5 maxWidth={500} />
    <Grid gutters="sm" guttersY="none">
      <Skeleton.Paragraph columns={2} size="sm" />
      <Skeleton.Paragraph columns={10} maxWidth={200} size="sm" />
      <Skeleton.Paragraph columns={2} size="sm" />
      <Skeleton.Paragraph columns={10} maxWidth={200} size="sm" />
      <Skeleton.Paragraph columns={2} size="sm" />
      <Skeleton.Paragraph columns={10} maxWidth={200} size="sm" />
      <Skeleton.Paragraph columns={2} size="sm" />
      <Skeleton.Paragraph columns={10} maxWidth={200} size="sm" />
    </Grid>
    <Separator size="xs" />
    <Skeleton.Paragraph maxWidth={600} size="md" />
    <Skeleton.Paragraph maxWidth={300} size="xs" />
  </>
)

export default function ProjectInfo({
  title,
  id,
  camera,
  place,
  estimatedImages,
  languages,
  actualImagesCaptured,
  loading = false,
}) {
  const projectStatus = useMemo(() => {
    if (!estimatedImages) return null
    const captureAvg = 8_000
    const percentageComplete = Math.floor((actualImagesCaptured / estimatedImages) * 100)
    const remainingImages = estimatedImages - actualImagesCaptured
    const estimatedCompletionDate = getEstimatedCompletionDate(remainingImages, captureAvg)

    return { percentageComplete, estimatedCompletionDate }
  }, [actualImagesCaptured, estimatedImages])

  const formattedEstimatedImages = estimatedImages ? new Intl.NumberFormat(i18n.lanuage).format(estimatedImages) : 0
  const formattedActualImagesCaptured = actualImagesCaptured
    ? new Intl.NumberFormat(i18n.language || navigator.language).format(actualImagesCaptured)
    : 0

  return (
    <DataPreview.Provider loading={loading}>
      <DataPreview.Loading>{loader}</DataPreview.Loading>
      <DataPreview.Ready>
        <H2 size="H5">{title || ''}</H2>
        <Grid gutters="sm" guttersY="none">
          <div css={styles.infoText} columns={2}>
            {transl.projectIdHeader()}
          </div>
          <div css={styles.infoText} columns={10}>
            <Link to={`${toSearchResultsWithProjectId(id)}`} target="_blank" linkName="See project in image search.">
              {id}
            </Link>
          </div>
          {camera && (
            <div css={styles.infoText} columns={2}>
              {transl.cameraInfo()}
            </div>
          )}
          {camera && (
            <div css={styles.infoText} columns={10}>
              {camera}
            </div>
          )}
          {place && (
            <div css={styles.infoText} columns={2}>
              {transl.location()}
            </div>
          )}
          {place && (
            <div css={styles.infoText} columns={10}>
              {place}
            </div>
          )}
          <div css={styles.infoText} columns={2}>
            {transl.language()}
          </div>
          <div css={styles.infoText} columns={10}>
            {new Intl.ListFormat(i18n.language).format(languages)}
          </div>
        </Grid>
        <Separator size="xs" />
        {estimatedImages > actualImagesCaptured && projectStatus && (
          <ProgressBar
            caption={transl.estimatedCompletion(formattedEstimatedImages, projectStatus?.estimatedCompletionDate)}
            total={estimatedImages}
            completed={actualImagesCaptured}
          />
        )}
        {estimatedImages <= actualImagesCaptured && (
          <ProgressBar
            caption={transl.estimatedImagesActualImages(formattedEstimatedImages, formattedActualImagesCaptured)}
            total={estimatedImages}
            completed={actualImagesCaptured}
          />
        )}
      </DataPreview.Ready>
    </DataPreview.Provider>
  )
}

ProjectInfo.propTypes = {
  title: PropTypes.string,
  id: PropTypes.string,
  camera: PropTypes.string,
  place: PropTypes.string,
  estimatedImages: PropTypes.number,
  languages: PropTypes.arrayOf(PropTypes.string),
  actualImagesCaptured: PropTypes.number,
  loading: PropTypes.bool,
}

function getEstimatedCompletionDate(remainingImages, captureAvg) {
  const weeksRemaining = Math.floor(remainingImages / captureAvg)
  const estimatedCompletionDate = new Date()
  const FRIDAY = 5
  const dayOfTheWeek = estimatedCompletionDate.getDay() // Sunday: 0, Monday: 1, ect.
  const daysRemainingInWeek = FRIDAY - dayOfTheWeek
  estimatedCompletionDate.setDate(estimatedCompletionDate.getDate() + (weeksRemaining * 7 + daysRemainingInWeek))
  const dateOptions = { month: '2-digit', day: '2-digit', year: 'numeric' }
  return estimatedCompletionDate.toLocaleDateString(i18n.language, dateOptions)
}
